import { useEffect, useRef } from "react";
import { loadScript } from "@/utils/loadScript";
import { FieldLabel } from "../ui/field";
import { useTranslation } from "react-i18next";

type Props = {
  visible: boolean;
  disabled?: boolean;
  environment: any;
  merchantId: string;
  merchantSiteId: string;
  onReadyChange?: (ready: boolean) => void;

  // keeps submit logic in parent
  onInit?: (ctx: {
    safeCharge: any;
    scFields: { cardNumber: any; cardExpiry: any; cardCvc: any };
  }) => void;
};

export function NuveiFields({
  visible,
  disabled,
  environment,
  merchantId,
  merchantSiteId,
  onReadyChange,
  onInit,
}: Props) {
  const { t, i18n } = useTranslation();
  const ccNumberRef = useRef<HTMLDivElement>(null);
  const ccExpiryRef = useRef<HTMLDivElement>(null);
  const ccCvcRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || disabled) return;

    let cancelled = false;

    (async () => {
      try {
        onReadyChange?.(false);

        await loadScript(
          "https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js"
        );

        if (cancelled) return;

        const sfc = (window as any).SafeCharge({
          merchantId,
          merchantSiteId,
          env: environment,
        });

        const fields = sfc.fields({
          locale: i18n.language,
          fonts: [{ cssUrl: "https://fonts.googleapis.com/css?family=Roboto" }],
        });

        // Prevent duplicate fields
        if (ccNumberRef.current) ccNumberRef.current.innerHTML = "";
        if (ccExpiryRef.current) ccExpiryRef.current.innerHTML = "";
        if (ccCvcRef.current) ccCvcRef.current.innerHTML = "";

        const cardNumber = fields.create("ccNumber", {});
        cardNumber.attach(ccNumberRef.current);

        const cardExpiry = fields.create("ccExpiration", {});
        cardExpiry.attach(ccExpiryRef.current);

        const cardCvc = fields.create("ccCvc", {});
        cardCvc.attach(ccCvcRef.current);

        onInit?.({
          safeCharge: sfc,
          scFields: { cardNumber, cardExpiry, cardCvc },
        });

        onReadyChange?.(true);
      } catch (e) {
        onReadyChange?.(false);
        // console.error(e);
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (ccNumberRef.current) ccNumberRef.current.innerHTML = "";
        if (ccExpiryRef.current) ccExpiryRef.current.innerHTML = "";
        if (ccCvcRef.current) ccCvcRef.current.innerHTML = "";
      } catch {}
      onReadyChange?.(false);
    };
  }, [
    visible,
    disabled,
    merchantId,
    merchantSiteId,
    environment,
    i18n.language,
  ]);

  if (!visible || disabled) return;

  return (
    <>
      {/* Card number */}
      <div className="mb-5 gap-1">
        <FieldLabel className="flex gap-1 mb-1">
          {t("models.paymentInfo.cardNumber")}
          <span className="text-red-500">*</span>
        </FieldLabel>
        <div
          ref={ccNumberRef}
          className="h-9 text-base rounded-md border border-input px-3 py-2"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div className="gap-1">
          <FieldLabel className="flex gap-1 mb-1">
            {t("models.paymentInfo.dateExpiration")}
            <span className="text-red-500">*</span>
          </FieldLabel>
          <div
            ref={ccExpiryRef}
            className="h-9 text-base rounded-md border border-input px-3 py-2"
          />
        </div>

        <div className="gap-1 items-end">
          <FieldLabel className="flex gap-1 mb-1">
            {t("models.paymentInfo.cvc")}
            <span className="text-red-500">*</span>
          </FieldLabel>
          <div
            ref={ccCvcRef}
            className="h-9 text-base rounded-md border border-input px-3 py-2"
          />
        </div>
      </div>
    </>
  );
}
